import { useCallback, useEffect, useMemo, useState } from 'react';
import { iniciais, podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import { lerRankingDaLoja } from '../../central';
import type { LinhaDoRankingDaLoja } from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { baixarCsv } from '../../design/csv';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { reaisCurto, semanaCurta } from '../../premiacao';
import { TopoMovel } from '../../shell/ShellBalconista';
import { lerRanking } from '../../vendas';
import type { LinhaDoRanking, PeriodoDoRanking, Ranking as Dados } from '../../vendas';
import './ranking.css';

/**
 * TELA 10 (parte B) — RANKING. "Ranking sem cassino": top 3 em três colunas do mesmo tamanho —
 * sem degrau, sem coroa, sem 3D; ouro, prata e bronze são só um filete de 3px. Quem está atrás
 * fica em cinza, nunca em vermelho de erro: ranking é comparação, não acusação.
 *
 * A ordem GERAL é a do 1.0 (vitaminas + pré-vencidos lançados, desempate pelas vitaminas) e vem do
 * servidor, assim como o corte: a premiação dos colegas não chega à balconista.
 *
 * RANKING SEPARADO (dono, 27/09/2026), só para quem administra: os campeões no topo e uma
 * classificação para cada categoria — LOJA (vendas do HOS: faturamento − devoluções; só quem vê a
 * loja), VITAMINA, PRÉ-VENCIDO e PARADÃO (unidades lançadas e a premiação daquele programa).
 */

const PERIODOS: readonly { p: PeriodoDoRanking; rotulo: string }[] = [
  { p: 'semana', rotulo: 'Semana' },
  { p: 'semana_passada', rotulo: 'Semana passada' },
  { p: 'mes', rotulo: 'Mês' },
];

type Categoria = 'geral' | 'loja' | 'vitamina' | 'pre_vencido' | 'paradao';

const CATEGORIAS: readonly { c: Categoria; rotulo: string; icone: NomeDoIcone }[] = [
  { c: 'geral', rotulo: 'Geral', icone: 'trophy' },
  { c: 'loja', rotulo: 'Loja', icone: 'money' },
  { c: 'vitamina', rotulo: 'Vitamina', icone: 'box' },
  { c: 'pre_vencido', rotulo: 'Pré-vencido', icone: 'clock' },
  { c: 'paradao', rotulo: 'Paradão', icone: 'cycle' },
];

/** Uma linha da classificação, igual para todas as categorias. */
interface Linha {
  readonly chave: string;
  readonly posicao: number;
  readonly nome: string;
  readonly codigo: string;
  /** O número que ordena (itens, unidades do programa ou faturamento). */
  readonly principal: number;
  readonly principalTexto: string;
  /** O detalhe embaixo do nome e na coluna do meio. */
  readonly detalhe: string;
  /** Premiação (ou ticket médio, na loja); null = não é seu. */
  readonly valor: string | null;
  readonly eu: boolean;
  /** Itens da meta semanal (só na geral). */
  readonly itensDaMeta: number | null;
  /** Loja: vende no HOS sem conta no InovaPay. */
  readonly semConta?: boolean;
}

const un = (n: number) => `${String(n)} un.`;
const valorTexto = (v: string | null) => (v !== null ? reaisCurto(Number(v)) : '—');

function linhasDoPrograma(
  linhas: readonly LinhaDoRanking[],
  qtd: (l: LinhaDoRanking) => number,
  valor: (l: LinhaDoRanking) => string | null,
): Linha[] {
  return [...linhas]
    .sort(
      (a, b) =>
        qtd(b) - qtd(a) ||
        Number(valor(b) ?? 0) - Number(valor(a) ?? 0) ||
        a.nome.localeCompare(b.nome, 'pt-BR'),
    )
    .map((l, k) => ({
      chave: l.accountId,
      posicao: k + 1,
      nome: l.nome,
      codigo: l.codigo,
      principal: qtd(l),
      principalTexto: String(qtd(l)),
      detalhe: `cód ${l.codigo}`,
      valor: valor(l),
      eu: l.eu,
      itensDaMeta: null,
    }));
}

function linhasDaLoja(linhas: readonly LinhaDoRankingDaLoja[]): Linha[] {
  return linhas.map((l) => ({
    chave: `hos:${l.codigo}`,
    posicao: l.posicao,
    nome: l.nome,
    codigo: l.codigo,
    principal: Number(l.faturamento),
    principalTexto: reaisCurto(Number(l.faturamento)),
    detalhe: `${String(l.cupons)} cupons · ${String(l.itens)} itens${
      Number(l.devolucoes) > 0 ? ` · −${reaisCurto(Number(l.devolucoes))} devol.` : ''
    }`,
    valor: l.ticketMedio,
    eu: false,
    itensDaMeta: null,
    semConta: !l.temConta,
  }));
}

/** Rótulos das colunas por categoria. */
const COLUNAS: Record<
  Categoria,
  { principal: string; meio: string; valor: string; ordem: string }
> = {
  geral: {
    principal: 'ITENS',
    meio: 'META',
    valor: 'PREMIAÇÃO',
    ordem: 'por itens vendidos (vitamina + pré-vencido)',
  },
  loja: {
    principal: 'FATURAMENTO',
    meio: 'CUPONS · ITENS',
    valor: 'TICKET MÉDIO',
    ordem: 'por faturamento no HOS (vendas − devoluções)',
  },
  vitamina: {
    principal: 'VITAMINAS',
    meio: '',
    valor: 'PREMIAÇÃO',
    ordem: 'por vitaminas lançadas',
  },
  pre_vencido: {
    principal: 'PRÉ-VENCIDOS',
    meio: '',
    valor: 'PREMIAÇÃO',
    ordem: 'por pré-vencidos lançados',
  },
  paradao: {
    principal: 'PARADÃO',
    meio: '',
    valor: 'PREMIAÇÃO',
    ordem: 'por itens do paradão lançados',
  },
};

export function Ranking({ eu, balconista }: { eu: Eu | null; balconista: boolean }) {
  const [periodo, setPeriodo] = useState<PeriodoDoRanking>('semana');
  const [categoria, setCategoria] = useState<Categoria>('geral');
  const [d, setD] = useState<Dados | null>(null);
  const [loja, setLoja] = useState<readonly LinhaDoRankingDaLoja[] | null>(null);
  const [erroLoja, setErroLoja] = useState(false);
  const [erro, setErro] = useState(false);
  const [busca, setBusca] = useState('');
  const [verZerados, setVerZerados] = useState(false);
  const separado = !balconista;
  const veLoja = separado && podeFazer(eu, 'loja.ver');

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    setLoja(null);
    setErroLoja(false);
    void lerRanking(periodo).then((r) => {
      if (!r.ok) return setErro(true);
      setD(r.dados);
      if (veLoja)
        void lerRankingDaLoja(r.dados.de, r.dados.ate).then((x) =>
          x.ok ? setLoja(x.dados.linhas) : setErroLoja(true),
        );
    });
  }, [periodo, veLoja]);
  useEffect(carregar, [carregar]);

  const cat: Categoria = balconista || (categoria === 'loja' && !veLoja) ? 'geral' : categoria;
  const semanal = periodo !== 'mes';
  const metaPct = (itens: number | null) =>
    itens !== null && d && semanal && d.metaSemanal > 0
      ? Math.round((itens / d.metaSemanal) * 100)
      : null;

  const porCategoria = useMemo((): Record<Categoria, Linha[]> => {
    const base = d?.linhas ?? [];
    return {
      geral: base.map((l) => ({
        chave: l.accountId,
        posicao: l.posicao,
        nome: l.nome,
        codigo: l.codigo,
        principal: l.itens,
        principalTexto: String(l.itens),
        detalhe: `${String(l.itens)} itens · cód ${l.codigo}`,
        valor: l.valor,
        eu: l.eu,
        itensDaMeta: l.itens,
      })),
      loja: loja ? linhasDaLoja(loja) : [],
      vitamina: linhasDoPrograma(
        base,
        (l) => l.vitaminas,
        (l) => l.valorVitamina,
      ),
      pre_vencido: linhasDoPrograma(
        base,
        (l) => l.preVencidos,
        (l) => l.valorPreVencido,
      ),
      paradao: linhasDoPrograma(
        base,
        (l) => l.paradao,
        (l) => l.valorParadao,
      ),
    };
  }, [d, loja]);

  const todas = porCategoria[cat];
  const vazio = d !== null && cat !== 'loja' && todas.every((l) => l.principal === 0);
  const carregandoLoja = cat === 'loja' && loja === null && !erroLoja;
  const top3 = todas.filter((l) => l.principal > 0 && !l.semConta).slice(0, 3);
  const eu_ = d?.linhas.find((l) => l.eu) ?? null;
  const acima = eu_ && eu_.posicao > 1 ? d?.linhas[eu_.posicao - 2] : null;
  const nomeDaCategoria = CATEGORIAS.find((x) => x.c === cat)?.rotulo ?? '';
  const titulo = `${
    periodo === 'mes'
      ? 'Ranking do mês'
      : periodo === 'semana_passada'
        ? 'Ranking da semana passada'
        : 'Ranking da semana'
  }${cat === 'geral' ? '' : ` · ${nomeDaCategoria}`}`;
  // Quem não lançou nada fica recolhido (a lista é de quem vendeu); a busca acha todo mundo.
  const zerados = todas.filter((l) => l.principal === 0).length;
  const linhas = todas.filter((l) =>
    busca.trim()
      ? l.nome.toLowerCase().includes(busca.trim().toLowerCase())
      : verZerados || l.principal > 0,
  );
  const col = COLUNAS[cat];
  /** Meta: "batida" quando chega a 100% (1380% não diz nada); abaixo, quanto falta. */
  const textoDaMeta = (itens: number, pct: number) =>
    pct >= 100
      ? 'batida'
      : d && d.metaSemanal > 0
        ? `faltam ${String(d.metaSemanal - itens)}`
        : `${String(pct)}%`;

  /** O 1º de cada categoria (o da loja só entre quem tem conta no InovaPay). */
  const campeoes = separado
    ? CATEGORIAS.filter((x) => x.c !== 'geral' && (x.c !== 'loja' || veLoja)).map((x) => {
        const primeiro = porCategoria[x.c].find((l) => l.principal > 0 && !l.semConta) ?? null;
        return { ...x, primeiro };
      })
    : [];

  const exportar = () => {
    if (!d) return;
    if (cat === 'geral')
      return baixarCsv(`ranking-${d.de}-a-${d.ate}.csv`, [
        [
          'Posição',
          'Balconista',
          'Código',
          'Vitaminas',
          'Pré-vencidos',
          'Paradão',
          'Itens',
          'Premiação',
          'Premiação vitamina',
          'Premiação pré-vencido',
          'Premiação paradão',
        ],
        ...d.linhas.map((l) => [
          l.posicao,
          l.nome,
          l.codigo,
          l.vitaminas,
          l.preVencidos,
          l.paradao,
          l.itens,
          l.valor === null ? null : Number(l.valor),
          l.valorVitamina === null ? null : Number(l.valorVitamina),
          l.valorPreVencido === null ? null : Number(l.valorPreVencido),
          l.valorParadao === null ? null : Number(l.valorParadao),
        ]),
      ]);
    if (cat === 'loja')
      return baixarCsv(`ranking-loja-${d.de}-a-${d.ate}.csv`, [
        [
          'Posição',
          'Vendedor',
          'Código',
          'Conta no InovaPay',
          'Faturamento',
          'Vendas',
          'Devoluções',
          'Cupons',
          'Itens',
          'Ticket médio',
          'Itens por cupom',
        ],
        ...(loja ?? []).map((l) => [
          l.posicao,
          l.nome,
          l.codigo,
          l.temConta ? 'sim' : 'não',
          Number(l.faturamento),
          Number(l.vendas),
          Number(l.devolucoes),
          l.cupons,
          l.itens,
          l.ticketMedio === null ? null : Number(l.ticketMedio),
          l.itensPorCupom,
        ]),
      ]);
    baixarCsv(`ranking-${cat.replace('_', '-')}-${d.de}-a-${d.ate}.csv`, [
      ['Posição', 'Balconista', 'Código', nomeDaCategoria, 'Premiação'],
      ...todas.map((l) => [
        l.posicao,
        l.nome,
        l.codigo,
        l.principal,
        l.valor === null ? null : Number(l.valor),
      ]),
    ]);
  };

  return (
    <>
      {balconista && <TopoMovel eu={eu} voltar="#/inicio" titulo="Ranking" />}
      <section className="rk">
        <div className="rk-topo">
          <div>
            <h1>{titulo}</h1>
            <div className="rk-sub">
              {d
                ? `${periodo === 'mes' ? d.de.slice(5, 7) + '/' + d.de.slice(0, 4) : semanaCurta(d.de, d.ate)} · ${String(
                    cat === 'loja' ? (loja?.length ?? 0) : d.linhas.length,
                  )} ${cat === 'loja' ? 'vendedores' : 'balconistas'} · ${col.ordem}`
                : 'Carregando…'}
            </div>
          </div>
          <div className="rk-acoes">
            <div className="rk-periodos" role="tablist">
              {PERIODOS.map((x) => (
                <button
                  key={x.p}
                  type="button"
                  role="tab"
                  aria-selected={periodo === x.p}
                  className={periodo === x.p ? 'rk-sel' : ''}
                  onClick={() => setPeriodo(x.p)}
                >
                  {x.rotulo}
                </button>
              ))}
            </div>
            {!balconista && (
              <button
                type="button"
                className="rk-botao"
                onClick={exportar}
                disabled={!d || (cat === 'loja' && !loja)}
              >
                Exportar
              </button>
            )}
          </div>
        </div>

        {separado && d && !erro && (
          <div className="rk-campeoes" role="list" aria-label="Campeões do período">
            {campeoes.map((x) => (
              <button
                key={x.c}
                type="button"
                role="listitem"
                className={`rk-campeao${cat === x.c ? ' rk-campeao-sel' : ''}`}
                onClick={() => setCategoria(x.c)}
              >
                <span className="rk-campeao-rotulo">
                  <Icone nome={x.icone} tamanho={13} traco={1.7} />
                  {x.c === 'loja'
                    ? 'MELHOR VENDEDOR DA LOJA'
                    : `MELHOR EM ${x.rotulo.toUpperCase()}`}
                </span>
                {x.c === 'loja' && loja === null && !erroLoja ? (
                  <span className="rk-campeao-nome rk-cinza">carregando…</span>
                ) : x.primeiro ? (
                  <>
                    <span className="rk-campeao-nome">
                      <span className="rk-avatar rk-avatar-p">{iniciais(x.primeiro.nome)}</span>
                      {x.primeiro.nome}
                    </span>
                    <span className="rk-campeao-num">
                      {x.c === 'loja' ? x.primeiro.principalTexto : un(x.primeiro.principal)}
                      {x.c !== 'loja' && x.primeiro.valor !== null && (
                        <em> · {valorTexto(x.primeiro.valor)}</em>
                      )}
                    </span>
                  </>
                ) : (
                  <span className="rk-campeao-nome rk-cinza">ninguém ainda</span>
                )}
              </button>
            ))}
          </div>
        )}

        {separado && (
          <div className="rk-periodos rk-categorias" role="tablist" aria-label="Categoria">
            {CATEGORIAS.filter((x) => x.c !== 'loja' || veLoja).map((x) => (
              <button
                key={x.c}
                type="button"
                role="tab"
                aria-selected={cat === x.c}
                className={cat === x.c ? 'rk-sel' : ''}
                onClick={() => setCategoria(x.c)}
              >
                {x.rotulo}
              </button>
            ))}
          </div>
        )}

        {erro ? (
          <ErroDeArea oQue="o ranking" aoTentar={carregar} />
        ) : cat === 'loja' && erroLoja ? (
          <ErroDeArea oQue="o ranking da loja" aoTentar={carregar} />
        ) : carregandoLoja || !d ? (
          <div className="cv-carregando" aria-busy="true" />
        ) : vazio ? (
          <div className="rk-vazio">
            <span className="rk-vazio-icone">
              <Icone nome="trophy" tamanho={18} traco={1.6} />
            </span>
            <strong>Ranking ainda não disponível</strong>
            <p>
              {cat === 'geral'
                ? 'Ninguém lançou venda neste período. A classificação aparece a partir do primeiro lançamento.'
                : `Ninguém lançou ${nomeDaCategoria.toLowerCase()} neste período.`}
            </p>
          </div>
        ) : (
          <>
            {balconista && eu_ && (
              <div className="rk-eu">
                <div className="rk-eu-linha">
                  <span className="rk-avatar rk-avatar-claro">{iniciais(eu_.nome)}</span>
                  <div className="rk-eu-pos">
                    <span>Sua posição</span>
                    <strong>
                      {eu_.posicao}º <em>de {d.linhas.length}</em>
                    </strong>
                  </div>
                  <div className="rk-eu-num">
                    <span>Itens</span>
                    <strong>{eu_.itens}</strong>
                  </div>
                  <div className="rk-eu-num">
                    <span>Premiação</span>
                    <strong className="rk-ouro-claro">
                      {eu_.valor ? reaisCurto(Number(eu_.valor)) : '—'}
                    </strong>
                  </div>
                </div>
                {acima && (
                  <div className="rk-eu-falta">
                    {acima.itens - eu_.itens + 1 > 0
                      ? `Faltam ${String(acima.itens - eu_.itens + 1)} itens para alcançar o ${String(acima.posicao)}º lugar.`
                      : `Empatado com o ${String(acima.posicao)}º lugar.`}
                  </div>
                )}
              </div>
            )}

            {cat === 'loja' && loja && loja.length > 0 && (
              <div className="rk-total-loja">
                <span className="rk-campeao-rotulo">TOTAL DA LOJA · TODOS OS VENDEDORES</span>
                <strong>{reaisCurto(loja.reduce((s, l) => s + Number(l.faturamento), 0))}</strong>
                <em>
                  {String(loja.reduce((s, l) => s + l.cupons, 0))} cupons ·{' '}
                  {String(loja.reduce((s, l) => s + l.itens, 0))} itens · ticket{' '}
                  {reaisCurto(
                    loja.reduce((s, l) => s + Number(l.vendas), 0) /
                      Math.max(
                        1,
                        loja.reduce((s, l) => s + l.cupons, 0),
                      ),
                  )}
                  {loja.some((l) => Number(l.devolucoes) > 0)
                    ? ` · −${reaisCurto(loja.reduce((s, l) => s + Number(l.devolucoes), 0))} em devoluções`
                    : ''}
                </em>
              </div>
            )}

            <div className="rk-top3">
              <div className="rk-rotulo-movel">TOP 3</div>
              {top3.map((l, i) => {
                const pct = metaPct(l.itensDaMeta);
                return (
                  <div
                    key={l.chave}
                    className={`rk-card rk-card-${String(i + 1)}${l.eu ? ' rk-card-eu' : ''}`}
                  >
                    <div className="rk-card-pessoa">
                      <span className="rk-avatar">{iniciais(l.nome)}</span>
                      <div>
                        <div className="rk-card-nome">
                          <span className="rk-pos">{l.posicao}º</span>
                          {l.nome}
                          {l.eu && <span className="rk-voce">VOCÊ</span>}
                        </div>
                        <div className="rk-card-sub">{l.detalhe}</div>
                      </div>
                    </div>
                    <div className="rk-card-numeros">
                      <div>
                        <span>
                          {col.principal.charAt(0) + col.principal.slice(1).toLowerCase()}
                        </span>
                        <strong>{l.principalTexto}</strong>
                      </div>
                      <div>
                        <span>{col.valor.charAt(0) + col.valor.slice(1).toLowerCase()}</span>
                        <strong className="rk-ouro">{valorTexto(l.valor)}</strong>
                      </div>
                      {pct !== null && l.itensDaMeta !== null && (
                        <div className="rk-card-meta">
                          <span>Meta</span>
                          <strong className={pct >= 100 ? 'rk-verde' : ''}>
                            {textoDaMeta(l.itensDaMeta, pct)}
                          </strong>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rk-tabela">
              <div className="rk-tabela-topo">
                <h2>Classificação completa{cat === 'geral' ? '' : ` · ${nomeDaCategoria}`}</h2>
                <label className="rk-busca">
                  <Icone nome="search" tamanho={14} traco={1.6} />
                  <input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar pessoa…"
                    aria-label="Buscar pessoa"
                  />
                </label>
              </div>
              <div className={`rk-lista${busca.trim() ? '' : ' rk-lista-sem-busca'}`}>
                <div className="rk-rotulo-movel">CLASSIFICAÇÃO</div>
                <div className="rk-cabeca">
                  <span>POS.</span>
                  <span>{cat === 'loja' ? 'VENDEDOR' : 'BALCONISTA'}</span>
                  <span className="rk-dir">{col.principal}</span>
                  <span>{cat === 'geral' ? (semanal ? col.meio : '') : col.meio}</span>
                  <span className="rk-dir">{col.valor}</span>
                </div>
                {linhas.map((l) => {
                  const pct = metaPct(l.itensDaMeta);
                  return (
                    <div
                      key={l.chave}
                      className={`rk-linha${l.eu ? ' rk-linha-eu' : ''}${l.semConta ? ' rk-linha-sem-conta' : ''}`}
                    >
                      <span className="rk-pos">{l.posicao}º</span>
                      <span className="rk-nome">
                        {l.nome}
                        {l.eu && <span className="rk-voce">VOCÊ</span>}
                        {l.semConta && <span className="rk-sem-conta">sem conta</span>}
                        <em>{l.detalhe}</em>
                      </span>
                      <span className="rk-dir rk-itens">{l.principalTexto}</span>
                      <span className="rk-meta">
                        {cat === 'loja' ? (
                          <span className="rk-cinza">{l.detalhe}</span>
                        ) : (
                          pct !== null &&
                          l.itensDaMeta !== null && (
                            <>
                              <span className="rk-barra">
                                <span
                                  className={
                                    pct >= 100
                                      ? 'rk-barra-verde'
                                      : pct < 50
                                        ? 'rk-barra-cinza'
                                        : 'rk-barra-perto'
                                  }
                                  style={{ width: `${String(Math.min(100, pct))}%` }}
                                />
                              </span>
                              <b className={pct >= 100 ? 'rk-verde' : ''}>
                                {textoDaMeta(l.itensDaMeta, pct)}
                              </b>
                            </>
                          )
                        )}
                      </span>
                      <span className="rk-dir rk-valor">{valorTexto(l.valor)}</span>
                    </div>
                  );
                })}
              </div>
              {!busca.trim() && zerados > 0 && (
                <button
                  type="button"
                  className="rk-zerados"
                  onClick={() => setVerZerados((v) => !v)}
                >
                  {verZerados
                    ? 'Esconder quem não lançou'
                    : `${String(zerados)} ${zerados === 1 ? 'pessoa' : 'pessoas'} sem lançamento no período — mostrar`}
                </button>
              )}
              <div className="rk-rodape">
                <span>
                  {linhas.length} de {todas.length} na tela
                </span>
                <span>
                  {cat === 'loja'
                    ? 'Vendas do HOS sem canceladas, menos as devoluções do vendedor. "Sem conta" não disputa o 1º lugar.'
                    : 'Ordem e critério de desempate vêm do sistema'}
                </span>
              </div>
            </div>
          </>
        )}
      </section>
    </>
  );
}
